import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

export default function StoreDetail() {
  useAuthGuard();

  const { id } = useParams();
  const [store, setStore] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');

        const [storeRes, statsRes] = await Promise.all([
          apiFetch(`${API_BASE_URL}/admin/stores/${id}`, { signal: ac.signal }),
          apiFetch(`${API_BASE_URL}/admin/stores/${id}/stats`, { signal: ac.signal }),
        ]);

        if (!storeRes.ok) throw new Error(`HTTP ${storeRes.status}`);
        const storeData = await storeRes.json();
        setStore(storeData.data);

        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData.data);
        }
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [id]);

  const fmtDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  };

  const statusBadge = (s) => {
    const map = { active: 'success', pending: 'warning', closed: 'danger', archived: 'secondary' };
    return map[s] || 'secondary';
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !store) {
    return (
      <Layout>
        <div className="alert alert-danger">{error || 'Store not found'}</div>
        <Link to="/stores" className="btn btn-outline-secondary">Back to Stores</Link>
      </Layout>
    );
  }

  const machineTypes = [
    { key: 'hookCount', label: 'Hook' },
    { key: 'bridgeCount', label: 'Bridge' },
    { key: 'threeClawCount', label: '3-Claw' },
    { key: 'twoClawCount', label: '2-Claw' },
    { key: 'peraRingCount', label: 'Pera Ring' },
    { key: 'suehirogariCount', label: 'Suehirogari' },
    { key: 'kakuritsuCount', label: 'Kakuritsu' },
    { key: 'pusherCount', label: 'Pusher' },
    { key: 'cuttingCount', label: 'Cutting' },
    { key: 'takoyakiCount', label: 'Takoyaki' },
    { key: 'jewelCatcherCount', label: 'Jewel Catcher' },
    { key: 'tenYenCatcherCount', label: '10-Yen Catcher' },
    { key: 'okashiTowerCount', label: 'Okashi Tower' },
    { key: 'craneCount', label: 'Crane (Other)' },
  ];

  const totalMachines = machineTypes.reduce((sum, m) => sum + (store[m.key] || 0), 0);

  return (
    <Layout>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1">
            {store.name}
            <span className={`badge bg-${statusBadge(store.status)} ms-2 fs-6`}>
              {store.status}
            </span>
            {store.ownerVerified && (
              <span className="badge bg-info ms-2 fs-6">Verified</span>
            )}
          </h2>
          <p className="text-muted mb-0">{store.address}</p>
        </div>
        <Link to="/stores" className="btn btn-outline-secondary">
          Back to Stores
        </Link>
      </div>

      <div className="row g-4">
        {/* Basic Info */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white fw-bold">Basic Info</div>
            <div className="card-body">
              <table className="table table-sm mb-0">
                <tbody>
                  <Row label="Name" value={store.name} />
                  <Row label="Name (EN)" value={store.nameEn} />
                  <Row label="Address" value={store.address} />
                  <Row label="Prefecture" value={store.prefecture} />
                  <Row label="City" value={store.city} />
                  <Row label="Chain" value={store.chain} />
                  <Row label="Store Type" value={store.storeType} />
                  <Row label="Place ID" value={store.placeId} mono />
                  <Row label="Source" value={store.source} />
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white fw-bold">Location</div>
            <div className="card-body">
              <table className="table table-sm mb-0">
                <tbody>
                  <Row label="Latitude" value={store.lat} />
                  <Row label="Longitude" value={store.lng} />
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Google Maps</td>
                    <td>
                      <a
                        href={`https://www.google.com/maps?q=${store.lat},${store.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in Maps
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white fw-bold">Contact</div>
            <div className="card-body">
              <table className="table table-sm mb-0">
                <tbody>
                  <Row label="Phone" value={store.phone} />
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Website</td>
                    <td>
                      {store.websiteUrl ? (
                        <a href={store.websiteUrl} target="_blank" rel="noopener noreferrer">
                          {store.websiteUrl}
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                  <Row label="Business Hours" value={store.businessHours} />
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Operations */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white fw-bold">Operations</div>
            <div className="card-body">
              <table className="table table-sm mb-0">
                <tbody>
                  <Row label="Open Time" value={store.openTime} />
                  <Row label="Close Time" value={store.closeTime} />
                  <Row label="Play Price" value={store.playPrice ? `${store.playPrice} yen` : '-'} />
                  <Row label="Parking" value={store.hasParking ? 'Yes' : 'No'} />
                  <Row
                    label="Payment Methods"
                    value={
                      store.paymentMethods && store.paymentMethods.length > 0
                        ? store.paymentMethods.join(', ')
                        : '-'
                    }
                  />
                  <Row label="Photo Allowed" value={store.photoAllowed ? 'Yes' : 'No'} />
                  <Row label="Staff Assist" value={store.staffAssist ? 'Yes' : 'No'} />
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Machine Types */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white fw-bold">
              Machines <span className="badge bg-primary ms-2">{totalMachines} total</span>
            </div>
            <div className="card-body">
              <table className="table table-sm mb-0">
                <tbody>
                  {machineTypes.map((m) => {
                    const count = store[m.key] || 0;
                    if (count === 0) return null;
                    return (
                      <tr key={m.key}>
                        <td className="text-muted" style={{ width: '60%' }}>{m.label}</td>
                        <td className="fw-medium">{count}</td>
                      </tr>
                    );
                  })}
                  {totalMachines === 0 && (
                    <tr>
                      <td colSpan={2} className="text-muted text-center">No machine data</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Ratings & Store Stats */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white fw-bold">Ratings & Stats</div>
            <div className="card-body">
              <table className="table table-sm mb-0">
                <tbody>
                  <Row label="Rating Avg" value={store.rating ? store.rating.toFixed(1) : '0.0'} />
                  <Row label="Review Count" value={store.reviewCount} />
                  <Row label="Photo Count" value={store.photoCount} />
                  <Row label="Contributor Count" value={store.contributorCount} />
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Related Data Stats (from API) */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white fw-bold">Related Data</div>
            <div className="card-body">
              {stats ? (
                <div className="row text-center g-3">
                  <StatBox label="Posts" value={stats.postsCount} color="primary" />
                  <StatBox label="Reviews" value={stats.reviewsCount} color="success" />
                  <StatBox label="Scans" value={stats.scansCount} color="info" />
                  <StatBox label="Favorites" value={stats.favoritesCount} color="warning" />
                  <StatBox label="Photos" value={stats.photosCount} color="secondary" />
                </div>
              ) : (
                <p className="text-muted mb-0">Stats not available</p>
              )}
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white fw-bold">Metadata</div>
            <div className="card-body">
              <table className="table table-sm mb-0">
                <tbody>
                  <Row label="Created At" value={fmtDate(store.createdAt)} />
                  <Row label="Updated At" value={fmtDate(store.updatedAt)} />
                  <Row label="Last Confirmed" value={fmtDate(store.lastConfirmedAt)} />
                  <Row label="Owner Verified" value={store.ownerVerified ? 'Yes' : 'No'} />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value, mono }) {
  const display = value == null || value === '' ? '-' : String(value);
  return (
    <tr>
      <td className="text-muted" style={{ width: '40%' }}>{label}</td>
      <td className={mono ? 'font-monospace small' : ''}>{display}</td>
    </tr>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="col">
      <div className={`border rounded p-2`}>
        <div className={`fs-4 fw-bold text-${color}`}>{value ?? 0}</div>
        <div className="small text-muted">{label}</div>
      </div>
    </div>
  );
}
